var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWeeklyDashboardContext } from '../context';
import formatISO from 'date-fns/formatISO';
import { useAssignmentsStreamData, useFetchStudentActivityStream, } from '../../ActivityStreamDashboard/shared';
import { usePostSeenCardsSessions } from '../../Common/hooks/usePostSeenCardsSessions';
var useStudentActivityStream = function (studentId) {
    var _a, _b;
    var _c = useWeeklyDashboardContext(), _d = _c.userData, role = _d.role, schoolId = _d.schoolId, _e = _c.calendarData, isWeekInThePast = _e.isWeekInThePast, _f = _e.weekRange, startDate = _f[0], endDate = _f[1];
    var _g = useState(false), filterActive = _g[0], setFilterActive = _g[1];
    var offsetTop = isWeekInThePast ? 107 : 67;
    var scrollToRef = useCallback(function (ref) {
        if (ref.current.getBoundingClientRect().top < offsetTop) {
            ref.current.scrollIntoView();
            window.scrollBy(0, -offsetTop);
        }
    }, [offsetTop]);
    var requestParams = useMemo(function () { return ({
        studentId: studentId,
        startDate: formatISO(startDate, { representation: 'date' }),
        endDate: formatISO(endDate, {
            representation: 'date',
        }),
        role: role,
        schoolId: schoolId,
    }); }, [studentId, startDate, endDate, role, schoolId]);
    var _h = useFetchStudentActivityStream(studentId, {
        startDate: formatISO(startDate, { representation: 'date' }),
        endDate: formatISO(endDate, {
            representation: 'date',
        }),
    }), _j = _h.response, studentActivityStream = _j === void 0 ? [] : _j, fetchState = _h.state;
    var _k = useAssignmentsStreamData({ requestParams: requestParams }), assignmentsWithSummary = _k[0], assignmentsState = _k[1].state;
    var assignments = useMemo(function () { var _a; return (_a = assignmentsWithSummary === null || assignmentsWithSummary === void 0 ? void 0 : assignmentsWithSummary.assignments) !== null && _a !== void 0 ? _a : []; }, [assignmentsWithSummary]);
    var _l = usePostSeenCardsSessions(), markCardsAsSeen = _l[0], seenRequestState = _l[1].state;
    useEffect(function () {
        if (seenRequestState !== 'idle')
            return;
        var activeStruggleCards = studentActivityStream
            .map(function (stream) {
            var struggleArr = [];
            stream.cards.forEach(function (card) {
                if (card.struggle &&
                    !stream.seenNotifications.includes(card.sessionId))
                    struggleArr.push(card.sessionId);
            });
            return struggleArr;
        })
            .flat();
        markCardsAsSeen(studentId, activeStruggleCards);
    }, [markCardsAsSeen, seenRequestState, studentActivityStream, studentId]);
    var struggledStreams = useMemo(function () {
        return studentActivityStream
            .filter(function (el) { return el.struggleTasks; })
            .map(function (el) { return (__assign(__assign({}, el), { cards: el.cards.filter(function (card) { return card.struggle; }) })); });
    }, [studentActivityStream]);
    var struggledAssignments = useMemo(function () {
        return assignments
            .filter(function (el) { return el.cards.some(function (card) { return card.struggled; }); })
            .map(function (el) { return (__assign(__assign({}, el), { cards: el.cards.filter(function (card) { return card.struggled; }) })); });
    }, [assignments]);
    var struggleTasksCount = struggledStreams.reduce(function (sum, el) { return sum + el.struggleTasks; }, 0) +
        ((_a = assignmentsWithSummary === null || assignmentsWithSummary === void 0 ? void 0 : assignmentsWithSummary.struggleTasksCount) !== null && _a !== void 0 ? _a : 0);
    var completedTasksCount = studentActivityStream.reduce(function (sum, el) { return sum + el.tasksCompleted; }, 0) +
        ((_b = assignments === null || assignments === void 0 ? void 0 : assignments.reduce(function (sum, el) { return sum + el.cards.length; }, 0)) !== null && _b !== void 0 ? _b : 0);
    var chosenStreams = filterActive ? struggledStreams : studentActivityStream;
    var chosenAssignments = filterActive ? struggledAssignments : assignments;
    var hasStreams = chosenStreams.length > 0;
    var hasAssignments = chosenAssignments.length > 0;
    var showFilter = struggleTasksCount > 0 && struggleTasksCount < completedTasksCount;
    var isLoading = fetchState === 'loading' || assignmentsState === 'loading';
    var isError = fetchState === 'error' || assignmentsState === 'error';
    var noTasks = !hasStreams && !hasAssignments && !isLoading && !isError;
    return {
        isLoading: isLoading,
        isError: isError,
        filterActive: filterActive,
        setFilterActive: setFilterActive,
        scrollToRef: scrollToRef,
        studentActivityStream: chosenStreams,
        assignments: chosenAssignments,
        struggleTasksCount: struggleTasksCount,
        completedTasksCount: completedTasksCount,
        noTasks: noTasks,
        showFilter: showFilter,
        hasStreams: hasStreams,
        hasAssignments: hasAssignments,
    };
};
export { useStudentActivityStream };
